import { useState } from "react";
import { Check, Copy } from "../../shared/Icon";
import { cn } from "../../../lib/cn";

/**
 * Tiny copy button sized for compact card headers (smaller than the shared
 * IconButton). Shows a check for ~1.2s after copying. Semantic <button> with
 * an aria-label, so it's keyboard-reachable.
 */
export function CopyButton({
  value,
  label = "Copy",
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={copied ? "Copied" : label}
      title={copied ? "Copied" : label}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        });
      }}
      className={cn(
        "inline-flex h-5 w-5 items-center justify-center rounded-sm shrink-0",
        "text-text-quaternary hover:text-text-primary hover:bg-bg-inset",
        "transition-colors duration-fast ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        copied && "text-status-success",
        className,
      )}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

/**
 * A tool target (path or command) that truncates cleanly. For paths we keep
 * the basename pinned and ellipsize the directory from the right, so the
 * filename never disappears. No interactive element of its own — safe to place
 * inside a larger toggle button (pair it with a sibling CopyButton).
 */
export function TargetText({
  value,
  kind = "text",
  className,
}: {
  value: string;
  kind?: "path" | "text";
  className?: string;
}) {
  if (!value) return null;
  const slash = kind === "path" ? value.lastIndexOf("/") : -1;
  const dir = slash >= 0 ? value.slice(0, slash + 1) : "";
  const base = slash >= 0 ? value.slice(slash + 1) : value;
  return (
    <span
      className={cn(
        "flex min-w-0 items-baseline overflow-hidden font-mono text-[12.5px]",
        className,
      )}
      title={value}
    >
      {dir && <span className="truncate text-text-quaternary">{dir}</span>}
      <span className="shrink-0 text-text-secondary">{base}</span>
    </span>
  );
}

